/**
 * Helpers for rendering companion-call history rows (#200 PR-B).
 *
 * Pure functions — no React, no fetch. Used by both the admin
 * user-detail history table and the family Portal "Recent calls"
 * timeline so the icon/label/copy stays in lockstep across surfaces.
 */

// Outcome → icon + short label + copy.
// Keep these stable: changing them shifts what families see when
// they open the Portal, and they may already have learned what each
// icon means.
export const OUTCOME_DISPLAY = {
  engaged: {
    icon: '✅',
    label: 'Engaged',
    family: 'Picked up and chatted',
    admin:  'Picked up + at least one user turn',
  },
  delivered_unconfirmed: {
    icon: '🔔',
    label: 'Delivered, no response',
    family: 'Picked up but did not speak',
    admin:  'Picked up; no transcribable user audio',
  },
  missed: {
    icon: '🔇',
    label: 'Missed',
    family: 'Did not pick up',
    admin:  'Server timed out — no answer in 60s',
  },
};

const TRIGGER_LABELS = {
  reminder: 'Reminder',
  wellbeing: 'Wellbeing check',
  inactivity: 'Inactivity nudge',
  weekly: 'Weekly catch-up',
  normal: 'User-initiated',
};

/**
 * Format the call's primary heading: "Reminder: take pills" or
 * "Wellbeing check" when there's no reminder task.
 */
export function formatCallHeading(call) {
  if (!call) return '';
  const trigger = TRIGGER_LABELS[call.call_type] || call.call_type || 'Call';
  if (call.reminder_task) {
    return `${trigger}: ${call.reminder_task}`;
  }
  return trigger;
}

/**
 * "Attempt 2 of 4" for retry-context display. Returns null when
 * there's only one attempt — the UI suppresses this row in that case.
 */
export function formatAttemptLabel(call) {
  if (!call) return null;
  const a = Number(call.attempt_number) || 1;
  const t = Number(call.total_attempts) || 1;
  if (t <= 1) return null;
  return `Attempt ${a} of ${t}${a > 1 ? ' (retry)' : ''}`;
}

/**
 * Outcome + icon for the right-side cell. Caller decides
 * which audience copy to use ('family' vs 'admin').
 */
export function formatOutcome(call, audience = 'family') {
  if (!call || !call.outcome) {
    return { icon: '·', label: 'Unknown', copy: 'Outcome unknown' };
  }
  const display = OUTCOME_DISPLAY[call.outcome];
  if (!display) {
    return { icon: '·', label: call.outcome, copy: call.outcome };
  }
  return {
    icon: display.icon,
    label: display.label,
    copy: display[audience] || display.family,
  };
}

/**
 * Format the localised when-string. Family-facing: "Wed May 6, 09:00".
 */
export function formatWhen(isoTimestamp) {
  if (!isoTimestamp) return '';
  const d = new Date(isoTimestamp);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Banner copy for the family Portal when voice_engagement is low or
 * inactive. Returns null when the user is engaging fine — the Portal
 * suppresses the banner in that case.
 */
export function nonEngagementBanner(voiceEngagement, displayName) {
  const name = displayName || 'your loved one';
  if (voiceEngagement === 'inactive') {
    return {
      severity: 'high',
      title: `${name} hasn't been picking up`,
      body: 'None of the recent companion calls reached them. The voice product may not be working for them right now — consider giving them a call yourself, or checking the tablet is on and online.',
    };
  }
  if (voiceEngagement === 'low') {
    return {
      severity: 'medium',
      title: `${name} has been picking up less often`,
      body: 'Recent companion calls have mostly gone unanswered or unconfirmed. Worth a check-in to see if everything is OK.',
    };
  }
  return null;
}
